import { Settings, User, Heart, BookOpen, Scale } from 'lucide-react';
import { type LucideIcon } from 'lucide-react';
import React, { useState, useCallback, useMemo } from 'react';

import { Button } from '@/components/design-system';
import {
  MARGIN_LEFT,
  PADDING_LEFT,
  PADDING_Y,
  SPACE_Y_SCALE,
  COLOR_SCALE,
  RADIUS_SCALE,
  MARGIN_TOP,
  PADDING_X,
  SPACE_X_SCALE,
  SIZE_SCALE,
  POSITION_SCALE,
} from '@/components/design-system/tokens';
import type { Tables } from '@/integrations/supabase/types';
import { createPageUrl } from '@/utils';

type ExtendedUser = Tables<'profiles'> & {
  role?: string;
  is_admin?: boolean;
  is_marketer?: boolean;
};

import { useEscapeKey, useArrowNavigation } from '../hooks/useKeyboardNav';

import { UserMenuToggleButton, ProfileSummary } from './user-menu/UserIdentity';
import { LogoutButton } from './user-menu/UserMenuActions';
import {
  getAdminLinks,
  getLegalLinks,
  getPartnerLinks,
  getPrimaryLinks,
  getResourceLinks,
  getToolsSettingsLinks,
} from './user-menu/UserMenuLinks';
import { MenuLink, MenuDivider, CollapsibleSection } from './user-menu/UserMenuSections';

interface MenuLinkData {
  key: string;
  to: string;
  icon: LucideIcon;
  label: string;
  children?: MenuLinkData[];
  external?: boolean;
  title?: string;
}

interface UserMenuProps {
  user: ExtendedUser | null;
  userDropdownOpen: boolean;
  setUserDropdownOpen: (open: boolean) => void;
  userDropdownRef: React.RefObject<HTMLDivElement>;
  handleLogout: () => void;
  handleSignup: () => void;
  handleLogin: () => void;
  headerOnLight: boolean;
  t: (
    page: string,
    context: string,
    fallback?: string | null,
    variables?: Record<string, unknown>
  ) => string;
}

// Helper function for user display information
const getUserDisplayInfo = (
  user: ExtendedUser | null,
  t: (page: string, key: string, fallback: string) => string
) => {
  const rawDisplayName =
    (user &&
      (user.full_name ? user.full_name.trim() : user.email ? user.email.trim() : undefined)) ??
    t('navigation', 'account', 'Account');
  const uppercaseDisplayName = rawDisplayName.toUpperCase();
  const avatarInitial = rawDisplayName.charAt(0).toUpperCase();

  return {
    rawDisplayName,
    uppercaseDisplayName,
    avatarInitial,
  };
};

// Helper function for user role checks
const getUserRoles = (user: ExtendedUser | null) => {
  const isAuthenticated = Boolean(user);
  const isAdmin = Boolean(user && (user.role === 'admin' || user.is_admin));
  const isMarketer = Boolean(user && user.is_marketer && !isAdmin);
  const isPartnerUser = Boolean(
    user &&
    user.is_partner &&
    (user.partner_category === 'marketer' ||
      (user.assigned_provider_ids &&
        Array.isArray(user.assigned_provider_ids) &&
        user.assigned_provider_ids.length > 0))
  );

  return {
    isAuthenticated,
    isAdmin,
    isMarketer,
    isPartnerUser,
  };
};

// Custom hook for managing menu expansion states
const useMenuExpansionStates = (setUserDropdownOpen: (open: boolean) => void) => {
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [partnerExpanded, setPartnerExpanded] = useState(false);
  const [resourcesExpanded, setResourcesExpanded] = useState(false);
  const [legalExpanded, setLegalExpanded] = useState(false);
  const [toolsSettingsExpanded, setToolsSettingsExpanded] = useState(false);

  // Close dropdown handler
  const closeDropdown = useCallback(() => {
    setUserDropdownOpen(false);
    setAdminExpanded(false);
    setPartnerExpanded(false);
    setResourcesExpanded(false);
    setLegalExpanded(false);
    setToolsSettingsExpanded(false);
  }, [setUserDropdownOpen]);

  return {
    adminExpanded,
    setAdminExpanded,
    partnerExpanded,
    setPartnerExpanded,
    resourcesExpanded,
    setResourcesExpanded,
    legalExpanded,
    setLegalExpanded,
    toolsSettingsExpanded,
    setToolsSettingsExpanded,
    closeDropdown,
  };
};

export const UserMenu = React.memo(
  ({
    user,
    userDropdownOpen,
    setUserDropdownOpen,
    userDropdownRef,
    handleLogout,
    handleSignup,
    handleLogin,
    headerOnLight,
    t,
  }: UserMenuProps) => {
    const {
      adminExpanded,
      setAdminExpanded,
      partnerExpanded,
      setPartnerExpanded,
      resourcesExpanded,
      setResourcesExpanded,
      legalExpanded,
      setLegalExpanded,
      toolsSettingsExpanded,
      setToolsSettingsExpanded,
      closeDropdown,
    } = useMenuExpansionStates(setUserDropdownOpen);

    useEscapeKey(userDropdownOpen, closeDropdown);
    useArrowNavigation(userDropdownOpen, 30, userDropdownRef);

    // User role and display logic
    const { isAuthenticated, isAdmin, isMarketer, isPartnerUser } = getUserRoles(user);
    const { rawDisplayName, uppercaseDisplayName, avatarInitial } = getUserDisplayInfo(user, t);

    // Navigation links
    const primaryLinks = useMemo(() => getPrimaryLinks(t), [t]);
    const resourceLinks = useMemo(() => getResourceLinks(t), [t]);
    const legalLinks = useMemo(() => getLegalLinks(t), [t]);
    const partnerLinks = useMemo(() => getPartnerLinks(t), [t]);
    const adminLinks = useMemo(
      () => getAdminLinks(t, { isAdmin, isMarketer }),
      [t, isAdmin, isMarketer]
    );
    const toolsSettingsLinks = useMemo(() => getToolsSettingsLinks(t), [t]);

    if (isAuthenticated) {
      return (
        <AuthenticatedMenuDropdown
          ref={userDropdownRef}
          adminLinks={adminLinks}
          adminSection={{
            expanded: adminExpanded,
            onToggle: () => setAdminExpanded(prev => !prev),
          }}
          anchorProps={{
            headerOnLight,
            isOpen: userDropdownOpen,
            onToggle: () => setUserDropdownOpen(!userDropdownOpen),
            displayName: rawDisplayName,
            uppercaseDisplayName,
            avatarInitial,
            user: user,
            t,
          }}
          dropdownState={{ isOpen: userDropdownOpen, closeDropdown }}
          legalSection={{
            expanded: legalExpanded,
            onToggle: () => setLegalExpanded(prev => !prev),
          }}
          legalLinks={legalLinks}
          partnerLinks={partnerLinks}
          partnerSection={{
            expanded: partnerExpanded,
            onToggle: () => setPartnerExpanded(prev => !prev),
          }}
          primaryLinks={primaryLinks}
          resourceLinks={resourceLinks}
          resourceSection={{
            expanded: resourcesExpanded,
            onToggle: () => setResourcesExpanded(prev => !prev),
          }}
          toolsSettingsLinks={toolsSettingsLinks}
          toolsSettingsSection={{
            expanded: toolsSettingsExpanded,
            onToggle: () => setToolsSettingsExpanded(prev => !prev),
          }}
          summaryProps={{
            user: user,
            displayName: rawDisplayName,
            uppercaseDisplayName,
            avatarInitial,
          }}
          isPartnerUser={isPartnerUser}
          showAdminSection={isAdmin || isMarketer}
          handleLogout={handleLogout}
          t={t}
        />
      );
    }

    return (
      <GuestMenu
        headerOnLight={headerOnLight}
        handleLogin={handleLogin}
        handleSignup={handleSignup}
        t={t}
      />
    );
  }
);

UserMenu.displayName = 'UserMenu';

interface AuthenticatedMenuDropdownProps {
  anchorProps: {
    headerOnLight: boolean;
    isOpen: boolean;
    onToggle: () => void;
    displayName: string;
    uppercaseDisplayName: string;
    avatarInitial: string;
    user: Tables<'profiles'> | null;
    t: (
      page: string,
      context: string,
      fallback?: string | null,
      variables?: Record<string, unknown>
    ) => string;
  };
  dropdownState: {
    isOpen: boolean;
    closeDropdown: () => void;
  };
  summaryProps: {
    user: Tables<'profiles'> | null;
    displayName: string;
    uppercaseDisplayName: string;
    avatarInitial: string;
  };
  primaryLinks: MenuLinkData[];
  resourceLinks: MenuLinkData[];
  legalLinks: MenuLinkData[];
  partnerLinks: MenuLinkData[];
  adminLinks: MenuLinkData[];
  toolsSettingsLinks: MenuLinkData[];
  toolsSettingsSection: {
    expanded: boolean;
    onToggle: () => void;
  };
  resourceSection: {
    expanded: boolean;
    onToggle: () => void;
  };
  legalSection: {
    expanded: boolean;
    onToggle: () => void;
  };
  partnerSection: {
    expanded: boolean;
    onToggle: () => void;
  };
  adminSection: {
    expanded: boolean;
    onToggle: () => void;
  };
  isPartnerUser: boolean;
  showAdminSection: boolean;
  handleLogout: () => void;
  t: (
    page: string,
    context: string,
    fallback?: string | null,
    variables?: Record<string, unknown>
  ) => string;
}

const AuthenticatedMenuDropdown = React.forwardRef<HTMLDivElement, AuthenticatedMenuDropdownProps>(
  (props, ref) => {
    const {
      anchorProps,
      dropdownState,
      summaryProps,
      primaryLinks,
      resourceLinks,
      legalLinks,
      partnerLinks,
      adminLinks,
      resourceSection,
      legalSection,
      partnerSection,
      adminSection,
      toolsSettingsLinks,
      toolsSettingsSection,
      isPartnerUser,
      showAdminSection,
      handleLogout,
      t,
    } = props;

    const { isOpen, closeDropdown } = dropdownState;
    const { expanded: resourcesExpanded, onToggle: toggleResources } = resourceSection;
    const { expanded: legalExpanded, onToggle: toggleLegal } = legalSection;
    const { expanded: partnerExpanded, onToggle: togglePartner } = partnerSection;
    const { expanded: adminExpanded, onToggle: toggleAdmin } = adminSection;
    const { expanded: toolsSettingsExpanded, onToggle: toggleToolsSettings } = toolsSettingsSection;

    return (
      <div className={POSITION_SCALE.relative} ref={ref}>
        <UserMenuToggleButton {...anchorProps} />

        {isOpen && (
          <div
            className={`absolute top-full right-0 ${MARGIN_TOP['2']} ${SIZE_SCALE.w64} max-w-[calc(100vw-2rem)] ${COLOR_SCALE.surface} ${RADIUS_SCALE.lg} shadow-xl border ${COLOR_SCALE['admin-border']} ${PADDING_Y['2']} ${PADDING_X['1']} z-[99999] max-h-[80vh] overflow-y-auto overflow-x-hidden`}
            role="menu"
            aria-label={t('aria_extended', 'user_menu_dropdown', 'User menu options')}
          >
            <ProfileSummary {...summaryProps} />

            <MenuLink
              to={createPageUrl('UserDashboard')}
              icon={User}
              label={t('navigation', 'dashboard', 'Dashboard')}
              onClick={closeDropdown}
              title={t('navigation', 'dashboard', 'Dashboard')}
            />
            <MenuLink
              to={createPageUrl('Favorites')}
              icon={Heart}
              label={t('navigation', 'favorites', 'Favorites')}
              onClick={closeDropdown}
              title={t('navigation', 'favorites', 'Favorites')}
            />

            <MenuDivider />

            {primaryLinks.map((link: MenuLinkData) => (
              <MenuLink
                key={link.key}
                to={link.to}
                icon={link.icon}
                label={link.label}
                onClick={closeDropdown}
                title={link.title}
              />
            ))}

            <MenuDivider />

            <CollapsibleSection
              icon={BookOpen}
              label={t('navigation', 'menu_resources', 'Resources')}
              isExpanded={resourcesExpanded}
              onToggle={toggleResources}
            >
              {resourceLinks.map((link: MenuLinkData) => (
                <MenuLink
                  key={link.key}
                  to={link.to}
                  icon={link.icon}
                  label={link.label}
                  onClick={closeDropdown}
                  variant="nested"
                  title={link.title}
                />
              ))}
            </CollapsibleSection>

            <CollapsibleSection
              icon={Scale}
              label={t('navigation', 'menu_legal_policies', 'Legal & Policies')}
              isExpanded={legalExpanded}
              onToggle={toggleLegal}
            >
              {legalLinks.map((link: MenuLinkData) => (
                <MenuLink
                  key={link.key}
                  to={link.to}
                  icon={link.icon}
                  label={link.label}
                  onClick={closeDropdown}
                  variant="nested"
                  title={link.title}
                />
              ))}
            </CollapsibleSection>

            <CollapsibleSection
              icon={Settings}
              label={t('navigation', 'tools_settings', 'Tools & Settings')}
              isExpanded={toolsSettingsExpanded}
              onToggle={toggleToolsSettings}
            >
              {toolsSettingsLinks.map((link: MenuLinkData) => (
                <MenuLink
                  key={link.key}
                  to={link.to}
                  icon={link.icon}
                  label={link.label}
                  onClick={closeDropdown}
                  variant="nested"
                  role={link.external ? 'link' : 'menuitem'}
                  title={link.title}
                />
              ))}
            </CollapsibleSection>

            {isPartnerUser && (
              <>
                <MenuDivider />
                <CollapsibleSection
                  icon={Settings}
                  label={t('navigation', 'partners', 'Partners')}
                  isExpanded={partnerExpanded}
                  onToggle={togglePartner}
                >
                  {partnerLinks.map((link: MenuLinkData) => (
                    <MenuLink
                      key={link.key}
                      to={link.to}
                      icon={link.icon}
                      label={link.label}
                      onClick={closeDropdown}
                      variant="nested"
                      title={link.title}
                    />
                  ))}
                </CollapsibleSection>
              </>
            )}

            {showAdminSection && (
              <>
                <MenuDivider />
                <CollapsibleSection
                  icon={Settings}
                  label={t('navigation', 'admin', 'Admin')}
                  isExpanded={adminExpanded}
                  onToggle={toggleAdmin}
                >
                  <div className={`${SPACE_Y_SCALE['1']} ${PADDING_Y['1']}`}>
                    {adminLinks.map((link: MenuLinkData) => (
                      <div key={link.key} className={`${SPACE_Y_SCALE['1']}`}>
                        <MenuLink
                          to={link.to}
                          icon={link.icon}
                          label={link.label}
                          onClick={closeDropdown}
                          title={link.title}
                        />
                        {Array.isArray(link.children) && link.children.length > 0 && (
                          <div
                            className={`${MARGIN_LEFT['4']} ${PADDING_LEFT['3']} border-l ${COLOR_SCALE['blue-100-border']}/80 ${SPACE_Y_SCALE['1']}`}
                          >
                            {link.children.map((child: MenuLinkData) => (
                              <MenuLink
                                key={child.key}
                                to={child.to}
                                icon={child.icon}
                                label={child.label}
                                onClick={closeDropdown}
                                variant="nested"
                                title={child.title}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              </>
            )}

            <MenuDivider />
            <LogoutButton
              onLogout={handleLogout}
              onClose={closeDropdown}
              label={t('navigation', 'logout', 'Log Out')}
            />
          </div>
        )}
      </div>
    );
  }
);

AuthenticatedMenuDropdown.displayName = 'AuthenticatedMenuDropdown';

interface GuestMenuProps {
  headerOnLight: boolean;
  handleSignup: () => void;
  handleLogin: () => void;
  t: (
    page: string,
    context: string,
    fallback?: string | null,
    variables?: Record<string, unknown>
  ) => string;
}

const GuestMenu = ({ headerOnLight, handleSignup, handleLogin, t }: GuestMenuProps) => (
  <div className={`hidden md:flex items-center ${SPACE_X_SCALE['2']}`}>
    <Button
      size="sm"
      onClick={handleSignup}
      variant="ghost"
      className={`text-xs font-medium transition-colors duration-300 ${
        headerOnLight
          ? `${COLOR_SCALE.foreground} hover:${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE['surface-subtle']}`
          : `text-white hover:text-white/80 hover:${COLOR_SCALE.surface}/20`
      } ${RADIUS_SCALE.full} ${PADDING_X['3']} ${PADDING_Y['1.5']} inline-flex items-center justify-center`}
    >
      {t('nav', 'sign_up', 'Sign Up')}
    </Button>
    <Button
      size="sm"
      onClick={handleLogin}
      className={`text-xs font-medium transition-all duration-300 ${
        headerOnLight
          ? `${COLOR_SCALE['shortcuts-bg']} hover:${COLOR_SCALE['shortcuts-hover']} ${COLOR_SCALE['shortcuts-text']} ${COLOR_SCALE['shortcuts-border']} shadow-lg`
          : `${COLOR_SCALE['shortcuts-bg']} hover:${COLOR_SCALE['shortcuts-hover']} ${COLOR_SCALE['shortcuts-text']} ${COLOR_SCALE['shortcuts-border']} shadow-lg`
      } ${RADIUS_SCALE.full} ${PADDING_X['3']} ${PADDING_Y['1.5']} inline-flex items-center justify-center`}
    >
      {t('nav', 'log_in', 'Log In')}
    </Button>
  </div>
);
